import React, { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { isEmpty } from 'lodash'
import Tooltip from './Tooltip'
import { SvgAccordionArrow } from '../assets/images'
import {
  BranchingChildType,
  ChildType,
  Confirmation,
  sidebarPopupConfig,
  urlPatternMatcher,
} from '../utils'
import ConfirmDialog from './ConfirmDialog'

const navSectionMatchesPath = (
  navItem: NavListTypes,
  pathList: string[]
): boolean => {
  if (pathList.includes(navItem.key)) return true
  if (!navItem.child?.length) return false
  return navItem.child.some((c) => {
    if (pathList.includes(c.key)) return true
    return c.child?.some((s) => pathList.includes(s.key)) ?? false
  })
}

interface SideNavLinksPropType {
  navItem: NavListTypes
  isOpen: boolean
  isActive: boolean
  pathList: string[]
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const SideNavLinks: React.FC<SideNavLinksPropType> = ({
  navItem,
  isActive = false,
  isOpen = false,
  pathList,
  setOpen,
}: SideNavLinksPropType) => {
  const navigate = useNavigate()
  const location = useLocation()

  const NavIcon = navItem?.icon
  const [childItems, setChildItems] = useState<boolean>(false)
  const [dialogInfo, setDialogInfo] = useState<{
    url: string[]
    message: string
  } | null>(null)
  const [nextUrl, setNextUrl] = useState<string | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  )

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const toggleChildSideNav = () => {
    setOpen(true)
    setChildItems(!childItems)
  }

  const handleLinkClick = (
    e: React.MouseEvent | React.KeyboardEvent<HTMLAnchorElement>,
    url: string,
    _key: string
  ) => {
    e.preventDefault()
    if (dialogInfo) {
      setConfirmDialogOpen(true)
      setNextUrl(url)
    } else {
      navigate(url)
    }
  }

  useEffect(() => {
    setChildItems(navSectionMatchesPath(navItem, pathList))
  }, [pathList])

  useEffect(() => {
    if (location.pathname) {
      setDialogInfo(urlPatternMatcher(location.pathname, sidebarPopupConfig))
    }
  }, [location.pathname])

  useEffect(() => {
    setExpandedItems(
      (navItem?.child ?? [])?.reduce(
        (
          acc: Record<string, boolean>,
          item: { key: string; child?: BranchingChildType[] }
        ) => {
          acc[item?.key] = Boolean(item?.child?.length)
          return acc
        },
        {}
      )
    )
  }, [navItem?.child])

  return (
    <>
      {navItem.child === undefined ? (
        <Link
          to={navItem?.link !== undefined ? navItem?.link : '#'}
          data-qa-id={navItem.key}
          onClick={(e) => handleLinkClick(e, navItem.link ?? '#', navItem.key)}
          onKeyDown={(e) => {
            if (e?.key === 'Enter') {
              handleLinkClick(e, navItem.link ?? '#', navItem.key)
              setTimeout(() => {
                setOpen(false)
              }, 0)
            }
          }}
        >
          <Tooltip content={navItem.label} position="right">
            <li
              className={`flex my-3 py-2 rounded max-h-[1.875rem] cursor-pointer px-2 items-center gap-2 transition-all  duration-300 ${
                isOpen ? `${navItem?.cssClasses}` : `mx-3 justify-center`
              } ${isActive ? 'bg-primary-25' : 'hover:bg-accent-50'}`}
              style={{
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.8, 1)',
              }}
            >
              <div className="flex gap-2 items-center">
                <div className="">
                  <NavIcon
                    className={` ${
                      isActive
                        ? 'svg-path-fill-primary'
                        : 'svg-path-fill-accent-600'
                    }`}
                  />
                </div>
                {isOpen ? (
                  <span
                    className={`truncate ${
                      isActive ? 'text-accent-900' : ''
                    } font-light`}
                  >
                    {navItem?.label}
                  </span>
                ) : null}
              </div>
            </li>
          </Tooltip>
        </Link>
      ) : (
        <>
          <Link
            to={navItem?.link !== undefined ? navItem?.link : '#'}
            data-qa-id={navItem.key}
            onClick={(e) => {
              if (navItem?.link === undefined) {
                e.preventDefault()
                toggleChildSideNav()
              }
            }}
          >
            <Tooltip content={navItem.label} position="right">
              <li
                className={`flex ${
                  navItem?.child && navItem?.child?.length > 0 ? 'mt-3' : 'my-3'
                } py-1 max-h-[1.875rem] rounded cursor-pointer px-2 items-center gap-2 transition-all ease-in-out duration-600 ${
                  isOpen
                    ? `${navItem?.cssClasses}`
                    : `mx-3 justify-center hover:bg-accent-50 items-center`
                }
          ${isActive ? 'bg-primary-25' : 'hover:bg-accent-50'}
          `}
                onClick={(e) => {
                  if (!navItem?.link) {
                    e.preventDefault()
                    setOpen(true)
                    setChildItems((prev) => !prev)
                  }
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <NavIcon
                    className={` ${
                      isActive
                        ? 'svg-path-fill-primary text-black'
                        : 'svg-path-fill-accent-600'
                    }`}
                  />
                  {isOpen ? (
                    <span className="truncate font-light">
                      {navItem?.label}
                    </span>
                  ) : null}
                </div>
                <div
                  className={`flex items-center gap-4 ml-auto ${
                    isOpen ? '' : 'hidden'
                  }`}
                >
                  {isOpen &&
                    navItem?.actionIcons?.map((item) => {
                      const ActionIcon = item?.icon
                      return (
                        <div key={item?.link}>
                          <Link
                            to={item?.link !== undefined ? item?.link : '#'}
                            data-qa-id={item?.key}
                            onClick={(e) =>
                              handleLinkClick(
                                e,
                                item?.link ?? '#',
                                item?.key ?? ''
                              )
                            }
                          >
                            <ActionIcon
                              className={`${
                                isActive
                                  ? 'svg-path-fill-primary text-black'
                                  : ''
                              }`}
                            />
                          </Link>
                        </div>
                      )
                    })}
                  {isOpen && (
                    <SvgAccordionArrow
                      className={`transform flex justify-center items-center transition-transform duration-200 ${
                        childItems ? 'rotate-180' : ''
                      }
                  `}
                    />
                  )}
                </div>
              </li>
            </Tooltip>
            {isOpen && childItems && (
              <div className="max-h-64 overflow-y-auto overflow-x-hidden">
                {navItem?.child
                  ?.filter((item) => item?.visible !== false)
                  .map((item) => (
                    <div key={item?.key} className="bg-tertiary-100">
                      {/* Parent Item */}
                      {item?.link ? (
                        <Link
                          to={item?.link}
                          data-qa-id={item?.key}
                          onClick={(e) =>
                            handleLinkClick(e, item?.link ?? '#', item?.key)
                          }
                          onKeyDown={(e) => {
                            if (e?.key === 'Enter') {
                              handleLinkClick(e, item?.link ?? '#', item?.key)
                              setTimeout(() => {
                                setOpen(false)
                              }, 0)
                            }
                          }}
                        >
                          <Tooltip content={item?.label} position="right">
                            <li
                              className={`flex py-2 rounded cursor-pointer px-2 items-center gap-2 transition-all ease-in-out  duration-600  
                    ${
                      pathList.includes(item?.key)
                        ? 'bg-primary-25 text-black'
                        : 'hover:bg-accent-50'
                    } 
                    ${isOpen ? '' : 'mx-3 justify-center'}`}
                            >
                              <div className="flex items-center ml-6">
                                {isOpen ? (
                                  <span className="truncate flex justify-center font-extraLight">
                                    {item?.label}
                                  </span>
                                ) : null}
                              </div>
                            </li>
                          </Tooltip>
                        </Link>
                      ) : (
                        <Tooltip content={item?.label} position="right">
                          <li
                            className={`flex py-3 font-light rounded px-2 items-center gap-2 transition-all ease-in-out duration-600  
              ${pathList.includes(item?.key) ? 'bg-primary-25' : ''} 
              ${isOpen ? '' : 'mx-3 justify-center'}
              ${expandedItems[item?.key] ? 'text-primary-500' : 'text-black'}`}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              toggleExpand(item?.key)
                            }}
                          >
                            <div className="flex items-center ml-6">
                              {isOpen ? (
                                <span className="truncate flex justify-center font-light">
                                  {item?.label}
                                </span>
                              ) : null}
                            </div>

                            {item?.child && (
                              <SvgAccordionArrow
                                className={`ml-auto transform transition-transform duration-600 ${
                                  expandedItems[item?.key] ? 'rotate-180' : ''
                                }`}
                              />
                            )}
                          </li>
                        </Tooltip>
                      )}

                      {expandedItems[item?.key] && item?.child && (
                        <ul className="ml-6 transition-all duration-600 ease-in-out">
                          {item?.child
                            ?.filter((subItem) => subItem?.visible !== false)
                            .map((subItem) => (
                              <Link
                                to={subItem?.link}
                                key={subItem?.key}
                                data-qa-id={subItem?.key}
                                onClick={(e) =>
                                  handleLinkClick(
                                    e,
                                    subItem?.link ?? '#',
                                    subItem?.key
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e?.key === 'Enter') {
                                    handleLinkClick(
                                      e,
                                      subItem?.link ?? '#',
                                      subItem?.key
                                    )
                                    setTimeout(() => {
                                      setOpen(false)
                                    }, 0)
                                  }
                                }}
                              >
                                <Tooltip
                                  content={subItem?.label}
                                  position="right"
                                >
                                  <li
                                    className={`py-1 rounded font-light cursor-pointer px-2 items-center transition-all ease-in-out duration-600 
                          ${
                            pathList.includes(subItem?.key)
                              ? 'bg-primary-25 text-black'
                              : ''
                          }`}
                                  >
                                    <div className="hover:bg-primary-25 py-1 rounded-md flex items-center ml-6">
                                      {isOpen && (
                                        <span className="ml-1 font-light truncate flex justify-center">
                                          {subItem?.label}
                                        </span>
                                      )}
                                    </div>
                                  </li>
                                </Tooltip>
                              </Link>
                            ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </Link>
          {isOpen && childItems && navItem?.showChildren && navItem?.children}
        </>
      )}
      <ConfirmDialog
        showCloseButtton={false}
        open={confirmDialogOpen && !isEmpty(dialogInfo)}
        confirmButtonText={Confirmation.YES}
        cancelButtonText={Confirmation.NO}
        message={dialogInfo?.message ?? ''}
        onConfirm={() => {
          if (nextUrl) {
            setConfirmDialogOpen(false)
            navigate(nextUrl)
            setDialogInfo(null)
            setNextUrl(null)
          }
        }}
        onCancel={() => {
          setConfirmDialogOpen(false)
          setNextUrl(null)
        }}
      />
    </>
  )
}

interface NavListTypes {
  label: string
  link?: string
  key: string
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  child?: ChildType[]
  cssClasses?: string
  showChildren?: boolean
  children?: ReactNode
  visible?: boolean

  // when you display extra icons in sidebar sections, you can use this following prop
  actionIcons?: {
    link?: string
    key?: string
    icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  }[]
}
interface SideNavPropTypes {
  navList: NavListTypes[]
  versionDetail: string
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const SideNav: React.FC<SideNavPropTypes> = ({
  navList,
  versionDetail,
  isOpen,
  setIsOpen,
}: SideNavPropTypes) => {
  const { pathname } = useLocation()
  const [pathList, setPathList] = useState<string[]>([])

  useEffect(() => {
    const pathNameList = pathname.split('/').filter((key) => key !== '')
    if (isEmpty(pathNameList)) {
      setPathList(['home'])
    } else {
      setPathList(pathNameList)
    }
  }, [pathname])

  return (
    <div className="z-[40]">
      <nav
        className={`h-[calc(100%-2.75rem)] fixed top-[3.65rem] left-0 border-accent-100 font-semibold text-navlink font-primary text-xs border py-3 transition-all ease-in-out duration-500 z-40 navbar border-r-2 ${
          isOpen ? 'w-64 nav-open bg-accent-0' : 'w-[3.75rem] bg-white'
        }`}
      >
        <div className={`flex flex-col h-full justify-between`}>
          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar mt-2 ${
              isOpen ? 'px-2' : ''
            }`}
          >
            {navList
              ?.filter((navItem) => navItem.visible !== false)
              .map((navItem) => (
                <SideNavLinks
                  pathList={pathList}
                  key={navItem?.key}
                  navItem={navItem}
                  isActive={navSectionMatchesPath(navItem, pathList)}
                  isOpen={isOpen}
                  setOpen={setIsOpen}
                />
              ))}
          </div>
          {versionDetail && isOpen ? (
            <div className="px-5 pt-3">
              <hr></hr>
              <p className="truncate pt-3 px-2 text-gray-400">
                {versionDetail}
              </p>
            </div>
          ) : null}
        </div>
      </nav>
    </div>
  )
}

export default SideNav
